<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="header-controls d-flex justify-content-between align-items-center mb-3">
        <span class="popup-title">{{ title }}</span>
        <div class="d-flex  position-absolute top-0 end-0">
          <button @click="downloadDiagram" class="btn-sm" title="Download Diagram">
            <i class="fas fa-cloud-download fa-sm"></i>
          </button>
          <button @click="zoomIn" class="btn-sm" title="Zoom In">
            <i class="fas fa-plus"></i>
          </button>
          <button @click="zoomOut" class="btn-sm" title="Zoom Out">
            <i class="fas fa-minus"></i>
          </button>
          <button @click="resetZoom" class="btn-sm" title="Reset Zoom">
            <i class="fas fa-undo"></i>
          </button>
          <button @click="close" class="btn-sm" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <hr class="custom-hr" />
      <div
        ref="mermaidContainer"
        class="mermaid"
        @wheel="handleZoom"
      ></div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import mermaid from "mermaid";

export default {
  name: "MermaidChart",
  props: ["data", "title"], // Add title as a prop
  data() {
    return {
      zoomLevel: 1, // Default zoom level
    };
  },
  mounted() {
    // Initialize Mermaid once when the component is mounted
    mermaid.initialize({ startOnLoad: false });
    this.renderMermaidChart();
  },
  watch: {
    data: "renderMermaidChart",
  },
  methods: {
    async renderMermaidChart() {
      if (typeof this.data !== "string" || !this.data.trim()) {
        console.error("Invalid Mermaid diagram data:", this.data);
        this.$refs.mermaidContainer.innerHTML = "<p>Invalid diagram data</p>";
        return;
      }

      await nextTick(); // Ensure the DOM is updated before rendering

      const container = this.$refs.mermaidContainer;
      if (!container) {
        console.error("Mermaid container is not defined.");
        return;
      }

      try {
        // Clear the container before rendering
        container.innerHTML = "";

        // Render the Mermaid diagram
        const { svg } = await mermaid.render("mermaidGraph", this.data);
        container.innerHTML = svg;

        // Apply the current zoom level to the SVG content
        this.applyZoom();
      } catch (error) {
        container.innerHTML = "<p>Failed to render diagram</p>";
      }
    },
    handleZoom(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1; // Zoom in or out
      this.zoomLevel = Math.max(this.zoomLevel + delta, 0.5); // Remove max zoom limit, keep min zoom at 0.5x
      this.applyZoom();
    },
    zoomIn() {
      this.zoomLevel += 0.1; // Remove max zoom limit
      this.applyZoom();
    },
    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5); // Keep min zoom at 0.5x
      this.applyZoom();
    },
    resetZoom() {
      this.zoomLevel = 1; // Reset zoom to default
      this.applyZoom();
    },
    applyZoom() {
      const svgElement = this.$refs.mermaidContainer.querySelector("svg");
      if (svgElement) {
        svgElement.style.transform = `scale(${this.zoomLevel})`;
        svgElement.style.transformOrigin = "top left"; // Always zoom from the top-left corner
      }
    },
    downloadDiagram() {
      const svgElement = this.$refs.mermaidContainer.querySelector("svg");
      if (!svgElement) {
        console.error("No SVG element found to download.");
        return;
      }

      const serializer = new XMLSerializer();
      const svgContent = serializer.serializeToString(svgElement);
      const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      // Use the title prop as the file name, fallback to 'mermaid-diagram' if not set
      const safeTitle = (this.title || 'mermaid-diagram').replace(/[\\/:*?"<>|]/g, '_');
      link.download = `${safeTitle}.svg`;
      link.click();

      URL.revokeObjectURL(url); // Clean up the URL object
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  /* Increase modal size to occupy at least 70% of the window */
  width: 70vw;
  height: 70vh;
  max-width: 90vw;
  max-height: 90vh;
  min-width: 400px;
  min-height: 300px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.popup-title {
  font-size: 1rem; /* Ensure consistent font size */
  font-weight: bold;
  color: #333;
  margin-left: -6px;
}

.header-controls {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
  margin-top: -23px;
}

.btn-sm {
  font-size: 1.2rem; /* Match previous button font size */
  border: none; /* Ensure no border is applied by default */
  background-color: transparent; /* Ensure no background color */
  color: #da6a26; /* Default button color */
  transition: transform 0.2s ease, color 0.2s ease; /* Smooth transition for hover effects */
}

.btn-sm:focus {
  outline: none;
  box-shadow: none; /* Remove focus shadow */
}

/* Re-enable hover styles after button loses focus */
.btn-sm:focus:not(:hover) {
  transform: none; /* Reset scale effect */
  color: #da6a26; /* Reset color to default */
}

.btn-sm:active,
.btn-sm:hover {
  outline: none;
  box-shadow: none; /* Remove focus and active shadows */
  border: none; /* Explicitly remove border */
  transform: scale(1.3); /* Scale effect on hover */
  color: black; /* Change color on hover */
}

.mermaid {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 80vh;
  margin: 0 auto;
  cursor: grab;
}

/* Custom scrollbar styles */
.mermaid::-webkit-scrollbar {
  width: 8px;
}

.mermaid::-webkit-scrollbar-thumb {
  background-color: #da6a26;
  border-radius: 4px;
}

.mermaid::-webkit-scrollbar-thumb:hover {
  background-color: #b3541e;
}

.mermaid::-webkit-scrollbar-track {
  background-color: #f1f1f1;
}

.custom-hr {
  border: none;
  border-top: 2px solid #da6a26;
  margin-left: -16px;
  margin-top: -7px;
  margin-right: -16px;
}
</style>
